import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../App.css';
import { useAtom, useAtomValue } from 'jotai';
import { userInfoAtom, searchQueryAtom } from '../../data/atoms/utilsAtoms';
import { useSavedVideos } from '@/v2/pages/AllVideos/hooks/useSavedVideos';
import { sendClickEvent, sendConversionEvent } from '@/service/algolia';
import api from '../../data/api';
import toast from 'react-hot-toast';

// Components
import VideoThumbnailCard from '../../v2/components/VideoThumbnailCard';
import AddToPlayListModal from '../playlist/personal-playlist/AddToPlayListModal';
import CustomPagination from '../Pagination/CustomPagination';
import checkApiStatus from '../SubscripStatus';
import { getSignupRoute } from '../../constants/featureFlags';
import MobileViewSearch from '../MobileViewSearch';

const SearchPageList = () => {
	const location = useLocation();
	const navigate = useNavigate();

	const userData = useAtomValue(userInfoAtom);
	const isLoggedIn = Boolean(userData?.token);
	const { savedVideoIds, setSavedVideoIds } = useSavedVideos(userData);

	const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
	const [exactMatches, setExactMatches] = useState([]);
	const [youMayAlsoLikeMatches, setYouMayAlsoLikeMatches] = useState([]);
	const [isSearching, setIsSearching] = useState(false);

	const [sortby, setSortBy] = useState('latest');
	const [currentPage, setCurrentPage] = useState(1);
	const perPage = 24;

	const [modal, setModal] = useState(false);
	const [videoData2, setVideoData2] = useState({});

	// Algolia queryID + Recommend state
	const [searchQueryID, setSearchQueryID] = useState(null);
	const [recommendations, setRecommendations] = useState([]);

	// Sync searchQuery with URL query parameter
	useEffect(() => {
		const searchquery = new URLSearchParams(location.search).get('search_query') || '';
		setSearchQuery(searchquery);
	}, [location.search]);

	// Debounced search trigger
	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			performSearch(searchQuery);
		}, 300);
		return () => clearTimeout(delayDebounceFn);
	}, [searchQuery]);

	// MongoDB fallback — called when Algolia fails or hits its limit
	const fetchMongoResults = async (query) => {
		const userId = userData.user?.userId ?? userData.user?._id ?? '';
		const response = await api.searchVideo(query, sortby, 199, 0, userId);
		const { videos: mongoVideos, queryID } = response.data;
		setSearchQueryID(queryID || null);
		// if (queryID && mongoVideos?.length > 0) {
		// 	try {
		// 		const recResponse = await api.getRecommendations(mongoVideos[0]._id);
		// 		setRecommendations(recResponse.data.recommendations || []);
		// 	} catch { setRecommendations([]); }
		// } else {
		// 	setRecommendations([]);
		// }
		setRecommendations([]);
		return (mongoVideos || []).map(v => ({
			...v,
			thumbnailLink: v.thumnailLink || v.thumbnailLink || '',
			tagsDetails: v.tagsDetails || v.tags || [],
			categoryDetails: v.categoryDetails || [],
		}));
	};

	const getCleanedSearchWords = (query) => {
		const queryLower = query.trim().toLowerCase();
		const excludedWords = ['intermediate', 'beginner', 'advanced', 'strength'];
		return queryLower
			.split(/\s+/)
			.map(w => w.replace(/[^a-z0-9]/g, ''))
			.filter(w => w.length > 0 && !excludedWords.includes(w));
	};

	const bucketVideos = (hits, queryLower, searchWords) => {
		const exactTitle = [], exactDesc = [], partialMatch = [], otherVideos = [];
		hits.forEach(video => {
			const title = (video.title || '').toLowerCase();
			const shortDesc = (video.shortDescription || '').toLowerCase();
			const description = (video.description || '').toLowerCase();
			const instructor = (video.instructor || '').toLowerCase();
			const tags = (video.tagsDetails || video.tags || []).map(t => (t?.name || (typeof t === 'string' ? t : '')).toLowerCase());
			const keywords = (video.keywords || []).map(k => (typeof k === 'string' ? k : k?.name || '').toLowerCase());
			if (title.includes(queryLower)) { exactTitle.push(video); return; }
			if (shortDesc.includes(queryLower) || description.includes(queryLower)) { exactDesc.push(video); return; }
			if (searchWords.length > 0) {
				const text = [title, shortDesc, description, instructor, ...tags, ...keywords].join(' ');
				if (searchWords.some(w => text.includes(w))) { partialMatch.push(video); return; }
			}
			otherVideos.push(video);
		});
		return { exactTitle, exactDesc, partialMatch, otherVideos };
	};

	const performSearch = async (query) => {
		try {
			setIsSearching(true);
			const queryLower = query.trim().toLowerCase();

			if (!queryLower) {
				setExactMatches([]);
				setYouMayAlsoLikeMatches([]);
				return;
			}

			// --- Try Algolia first ---
			// Algolia search is temporarily disabled. MongoDB fallback below is now the active search path.
			// try {
			// 	const index = getVideosIndex();
			// 	const currentTimestamp = Math.floor(Date.now() / 1000);
			// 	const filterString = `isActive:true AND isDeleted:false AND releaseDateTimeTimestamp <= ${currentTimestamp}`;
			// 	const searchWords = getCleanedSearchWords(query);
			// 	const algoliaQuery = searchWords.length > 0 ? searchWords.join(' ') : '';
			//
			// 	const result = await index.search(algoliaQuery, { filters: filterString, hitsPerPage: 1000 });
			// 	const fallbackResult = await index.search('', { filters: filterString, hitsPerPage: 1000 });
			//
			// 	const hitIds = new Set(result.hits.map(h => h.objectID));
			// 	const uniqueFallbackHits = (fallbackResult.hits || []).filter(h => !hitIds.has(h.objectID));
			//
			// 	const isClass = hit => hit.type === 'class' || !hit.categoryId;
			// 	const mapHit = hit => ({
			// 		...hit,
			// 		_id: hit.objectID,
			// 		thumbnailLink: hit.thumnailLink || hit.thumbnailLink || '',
			// 		tags: hit.tags || [],
			// 		tagsDetails: hit.tags || [],
			// 		categoryDetails: [],
			// 		categoryId: hit.categoryId || (isClass(hit) ? 'classes' : null),
			// 		categoryName: hit.categoryName || (isClass(hit) ? 'Classes' : ''),
			// 	});
			//
			// 	const allHits = [...result.hits.map(mapHit), ...uniqueFallbackHits.map(mapHit)];
			// 	const { exactTitle, exactDesc, partialMatch, otherVideos } = bucketVideos(allHits, queryLower, searchWords);
			//
			// 	setExactMatches(exactTitle);
			// 	setYouMayAlsoLikeMatches({ exactDesc, partialMatch, otherVideos });
			// 	setSearchQueryID(null);
			// 	setRecommendations([]);
			// 	setCurrentPage(1);
			// 	window.history.replaceState(null, '', `/result?search_query=${encodeURIComponent(query)}`);
			// 	return;
			// } catch (algoliaError) {
			// 	console.warn('[Search] Algolia failed, falling back to MongoDB:', algoliaError.message);
			// }

			// --- MongoDB fallback ---
			const searchWords = getCleanedSearchWords(query);
			const mongoHits = await fetchMongoResults(query);
			const { exactTitle, exactDesc, partialMatch, otherVideos } = bucketVideos(mongoHits, queryLower, searchWords);
			setExactMatches(exactTitle);
			setYouMayAlsoLikeMatches({ exactDesc, partialMatch, otherVideos });
			setCurrentPage(1);
			window.history.replaceState(null, '', `/result?search_query=${encodeURIComponent(query)}`);
		} catch (err) {
			console.error('[Search] MongoDB search failed:', err.message);
		} finally {
			setIsSearching(false);
		}
	};

	// Pagination
	const allResults = exactMatches;
	const totalPages = Math.ceil(allResults.length / perPage);
	const paginatedResults = allResults.slice((currentPage - 1) * perPage, currentPage * perPage);

	// "You May Also Like" — desc + partial + other, capped at 15
	const youMayAlsoLike = [
		...(youMayAlsoLikeMatches?.exactDesc || []),
		...(youMayAlsoLikeMatches?.partialMatch || []),
		...(youMayAlsoLikeMatches?.otherVideos || []),
	].slice(0, 15);

	const handlePageChange = newPage => {
		window.scrollTo(0, 0);
		setCurrentPage(Math.max(1, newPage));
	};

	const handleFilterChange = event => setSortBy(event.target.value);

	const handelSubmitPlaylist = async _id => {
		try {
			const response = await api.videoPlayerPage(_id, userData?.token);
			if (response.status === 200) {
				setModal(false);
				setVideoData2(response.data[0]);
				setModal(true);
			}
		} catch {
			setModal(false);
			toast.error('Something went wrong', { position: 'top-center', duration: 3000 });
		}
	};

	const handleLikeDislike = async (id, video) => {
		const isLiked =
			video.videoDetails?.savedvideo ||
			video.savedvideo ||
			(savedVideoIds?.length > 0 && savedVideoIds.includes(video?.videoDetails?._id || video?._id));
		try {
			if (isLiked) {
				const response = await api.unsaveWishlist(id, userData?.token);
				if (response.status === 200) setSavedVideoIds(prev => prev.filter(v => v !== id));
			} else {
				const response = await api.saveWishlist(id, userData?.token);
				if (response.status === 200) {
					setSavedVideoIds(prev => [...prev, id]);
				}
			}
		} catch (error) {
			console.error('Error updating wishlist:', error);
		}
	};

	const handlePlayerVideo = async (id, position) => {
		sendClickEvent({
			objectID: String(id),
			userToken: userData?.user?._id || userData?.user?.userId || 'anonymous',
			...(searchQueryID && { queryID: searchQueryID }),
			...(position && { position }),
		});
		const apistatus = await checkApiStatus(userData?.token);
		if (isLoggedIn) {
			if (apistatus) {
				navigate(`/PlayerPage?v=${id}`);
			} else {
				navigate('/Plans');
			}
		} else {
			navigate(getSignupRoute());
		}
	};

	return (
		<>
			{/* Mobile */}
			<div id="c-mobile_search">
				<div className="row m-0 c-tab_box">
					<div className="p-0">
						<div className="c-filter_btn">
							<MobileViewSearch />
							<div className="c-filter_btn-one c-mobile_filter_btn2">
								<span>Sort by</span>
								<select className="filterby" onChange={handleFilterChange} value={sortby}>
									<option value={'latest'}>Latest First</option>
									<option value={'oldest'}>Oldest First</option>
									<option value={'title_asc'}>Title (A - Z)</option>
									<option value={'title_desc'}>Title (Z - A)</option>
									<option value={'long_duration'}>Long duration</option>
									<option value={'short_duration'}>Short duration</option>
								</select>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Desktop */}
			<div id="c-mobile_tab_hide" className="c-tab_cata c-tab_cata_across pb-2">
				<div className="row m-0 c-tab_box">
					<div className="col-md-8">
						<div className="c-filter_btn c-filter_btn-lessons">
							<div className="c-filter_btn-one">
								<span>Sort by</span>
								<select className="filterby" onChange={handleFilterChange} value={sortby}>
									<option value={'latest'}>Latest First</option>
									<option value={'oldest'}>Oldest First</option>
									<option value={'title_asc'}>Title (A - Z)</option>
									<option value={'title_desc'}>Title (Z - A)</option>
									<option value={'long_duration'}>Long duration</option>
									<option value={'short_duration'}>Short duration</option>
								</select>
							</div>
							<div></div>
						</div>
					</div>
				</div>
			</div>

			<div>
				<div className="tab-content">
					<div id="menu2" className="tab-pane fade active show">
						<div>
							<div className="c-video">
								<div className="row c-video_textrow c-video_list pb-2 pt-3">
									<div className="col-12 c-video_textcol c-mobile_videotext-1 search">
										<h4>Search Matches</h4>
									</div>
								</div>

								<div className="c-video_list">
									<div className="row m-0">
										{paginatedResults.map((video, index) => (
											<div key={video._id} className="col-md-3 c-mobile_col">
												<div className="c-video_box">
													<VideoThumbnailCard
														content={video}
														isLoggedIn={userData?.token}
														handlePlayerVideo={id => handlePlayerVideo(id, (currentPage - 1) * perPage + index + 1)}
														handelSubmitPlaylist={handelSubmitPlaylist}
														handleSaveUnsave={handleLikeDislike}
														savedVideos={savedVideoIds}
														modal={modal}
														setModal={setModal}
														setVideoData={setVideoData2}
														titleStyles={{ fontSize: 'clamp(1.15rem, 1.2vw, 1.6rem)' }}
													/>
												</div>
											</div>
										))}
									</div>
								</div>

								{totalPages > 1 && (
									<CustomPagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} />
								)}

								{/* You May Also Like — from search bucketing or Algolia Recommend
								{(youMayAlsoLike.length > 0 || recommendations.length > 0) && (
									<div className="c-video mt-2">
										<hr style={{ borderColor: 'rgba(255,255,255,0.12)', margin: '0 0 8px 0' }} />
										<div className="row c-video_textrow c-video_list pb-2 pt-2">
											<div className="col-12 c-video_textcol c-mobile_videotext-1 search">
												<h4>You May Also Like</h4>
											</div>
										</div>
										<div className="c-video_list">
											<div className="row m-0">
												{(recommendations.length > 0 ? recommendations : youMayAlsoLike).map((video, index) => (
													<div key={video._id || index} className="col-md-3 c-mobile_col">
														<div className="c-video_box">
															<VideoThumbnailCard
																content={video}
																isLoggedIn={userData?.token}
																handlePlayerVideo={id => handlePlayerVideo(id)}
																handelSubmitPlaylist={handelSubmitPlaylist}
																handleSaveUnsave={handleLikeDislike}
																savedVideos={savedVideoIds}
																modal={modal}
																setModal={setModal}
																setVideoData={setVideoData2}
																titleStyles={{ fontSize: 'clamp(1.15rem, 1.2vw, 1.6rem)' }}
															/>
														</div>
													</div>
												))}
											</div>
										</div>
									</div>
								)} */}
							</div>
						</div>
					</div>
				</div>
			</div>

			<AddToPlayListModal modal={modal} setModal={setModal} activeObject={videoData2} viewData={null} />
		</>
	);
};

export default SearchPageList;
